import { ReactNode } from 'react';

import { Box, Flex, Heading, LinkBox, LinkOverlay, Skeleton, Tag, Text, useToken } from '@chakra-ui/react';
import { ArrowRightIcon } from '@udacity/chakra-uds-icons';

import { Card } from '~/components/card';
import { Carousel } from '~/components/carousel';
import { SanityImage } from '~/components/sanity-image';
import { trackEvent } from '~/features/analytics/track-event';
import { getIndustryDisplayText, getKnowledgeAreaDisplayText } from '~/features/enterprise-resource/data';
import { EnterpriseCenterResource } from '~/features/page-template/templates/enterprise-resource-center/types';

export type AIResourcesSectionProps = {
  title: ReactNode;
  resources: EnterpriseCenterResource[];
  theme?: 'default' | 'blue' | 'agentic-blue';
};

export function AIResourcesSection({ title, resources = [], theme = 'default' }: AIResourcesSectionProps) {
  const [seafoam600] = useToken('colors', ['accent.sea-foam.600']);
  function getAccentColor(theme: AIResourcesSectionProps['theme']) {
    switch (theme) {
      case 'agentic-blue':
        return '#006AFF';
      case 'blue':
        return 'blue.500';
      default:
        return seafoam600;
    }
  }
  const accentColor = getAccentColor(theme);

  return (
    <Box
      as='section'
      bgColor='white'
      padding={{
        base: '40px 0px',
        xl: '80px 0px',
      }}
    >
      <Carousel
        scrollBarColorOverride={accentColor}
        theme='light'
        title={typeof title === 'string' ? title : undefined}
        titleAs='h2'
        titleSize='marketing-4xl'
        titleSlot={typeof title !== 'string' ? title : undefined}
        items={resources.map((resource) => {
          const surTitle = resource.knowledgeAreas
            ? getKnowledgeAreaDisplayText(resource.knowledgeAreas[0])
            : resource.industries
              ? getIndustryDisplayText(resource.industries[0])
              : '';
          return (
            <LinkBox key={resource._id} role='group'>
              <Card as='article' border='none' display='flex' flexDir='column' gap={6} minHeight='472px' width='362px'>
                <Box borderRadius='8px 16px' height='248px' overflow='hidden' position='relative'>
                  {resource.resourceCenterImage && (
                    <SanityImage
                      image={resource.resourceCenterImage}
                      sizes='352px'
                      style={{
                        objectFit: 'cover',
                        pointerEvents: 'none',
                        objectPosition: 'center',
                      }}
                      fill
                    />
                  )}
                  {!resource.resourceCenterImage && <Skeleton height='full' width='full' />}
                  <Box
                    bgGradient='linear(to-b, transparent, white, transparent)'
                    bottom={0}
                    height='120%'
                    mixBlendMode='soft-light'
                    opacity={0.3}
                    pointerEvents='none'
                    position='absolute'
                    right={0}
                    transform='translateX(50%) translateY(100%) rotate(-20deg)'
                    transition='transform 0.3s ease-in-out'
                    width='200%'
                    _groupHover={{
                      transform: 'translateX(10%) translateY(-10%) rotate(-20deg)',
                    }}
                  />
                </Box>
                <Flex alignItems='flex-start' flexDir='column' gap={4}>
                  <Tag>{surTitle}</Tag>
                  <Heading
                    as={LinkOverlay}
                    href={resource.linkHref}
                    size='marketing-xl'
                    onClick={() =>
                      trackEvent({
                        name: 'skills_page_cta_clicked',
                        type: 'click',
                        label: 'Resources',
                        action: resource.type,
                        value: resource.title,
                      })
                    }
                  >
                    {resource.title}
                  </Heading>
                  <Flex alignItems='center' marginTop='auto' minHeight='48px'>
                    <Text alignItems='center' color={accentColor} display='inline-flex' fontWeight='600' gap={1}>
                      {resource.linkText} <ArrowRightIcon height='24px' width='24px' />
                    </Text>
                  </Flex>
                </Flex>
              </Card>
            </LinkBox>
          );
        })}
        navButtonProps={
          theme === 'agentic-blue'
            ? {
                boxShadow: 'inset 0 0 0 2px #006AFF',
                sx: {
                  '&:hover:not(:disabled)': {
                    backgroundColor: '#0052CC',
                    color: 'white !important',
                  },
                },
              }
            : {
                colorScheme: theme === 'default' ? 'sea-foam' : 'blue',
              }
        }
      />
    </Box>
  );
}
